import { useEffect, useState } from "react"
import { useMenuData } from "../offline/menuData"
import {
  firstSyncMessage,
  formatAsOf,
  formatBytes,
  formatRelative,
  photosLabel
} from "../models/syncStatus"
import { countCachedImages } from "../offline/imageCache"
import useCloseRequest from "../hooks/useCloseRequest"
import "./OfflineDataSheet.css"

// How long "Clear Offline Data" waits for the confirming second tap.
const CONFIRM_WINDOW_MS = 4000

// Mirrors LanguageSheet: what this device holds for offline use, with Sync Now and Clear.
export default function OfflineDataSheet({ onClose }) {
  const { menu, sync, isOnline, now, imageProgress, pending, syncNow, clearOfflineData } =
    useMenuData()
  const [storageUsed, setStorageUsed] = useState(null)
  const [cachedPhotos, setCachedPhotos] = useState(null)
  const [isConfirmingClear, setIsConfirmingClear] = useState(false)
  useCloseRequest(true, onClose)

  useEffect(() => {
    let isCurrent = true
    navigator.storage
      ?.estimate?.()
      .then((estimate) => {
        if (isCurrent) setStorageUsed(estimate?.usage ?? null)
      })
      .catch(() => {})
    countCachedImages().then((count) => {
      if (isCurrent) setCachedPhotos(count)
    })
    return () => {
      isCurrent = false
    }
  }, [menu?.generatedAt, imageProgress.saved, pending.total])

  useEffect(() => {
    if (!isConfirmingClear) return undefined
    const timer = setTimeout(() => setIsConfirmingClear(false), CONFIRM_WINDOW_MS)
    return () => clearTimeout(timer)
  }, [isConfirmingClear])

  const isSyncing = sync.phase === "syncing" || sync.phase === "loading"

  const rows = [
    {
      label: "Last synced",
      value: sync.lastSyncedAt
        ? `${formatRelative(sync.lastSyncedAt, now)} · ${formatAsOf(sync.lastSyncedAt)}`
        : "Not yet"
    },
    { label: "Menu items", value: menu ? String(menu.itemCount) : "—" },
    { label: "Storage used", value: storageUsed === null ? "—" : formatBytes(storageUsed) },
    { label: "Offline photos", value: photosLabel(imageProgress, cachedPhotos) },
    { label: "Flags waiting to send", value: String(pending.flags) }
  ]

  const clear = async () => {
    if (!isConfirmingClear) {
      setIsConfirmingClear(true)
      return
    }
    setIsConfirmingClear(false)
    await clearOfflineData()
  }

  return (
    <div className="offline-sheet" role="dialog" aria-modal="true" aria-label="Offline Data">
      <button type="button" className="offline-sheet__scrim" aria-label="Close" onClick={onClose} />

      <div className="offline-sheet__panel">
        <span className="offline-sheet__grabber" aria-hidden="true" />
        <h2 className="offline-sheet__title t-y24">Offline Data</h2>
        <span className="offline-sheet__rule" />

        <dl className="offline-sheet__rows">
          {rows.map((row) => (
            <div className="offline-sheet__row" key={row.label}>
              <dt className="offline-sheet__label t-p14">{row.label}</dt>
              <dd className="offline-sheet__value t-p14">{row.value}</dd>
            </div>
          ))}
        </dl>

        {!isOnline && (
          <p className="offline-sheet__notice t-p12" role="status">
            You're offline. The saved menu and search still work.
          </p>
        )}
        {isOnline && sync.lastError && (
          <p className="offline-sheet__notice offline-sheet__notice--error t-p12" role="status">
            {menu ? sync.lastError.message : firstSyncMessage(sync.lastError)}
          </p>
        )}

        <div className="offline-sheet__actions">
          <button
            type="button"
            className="offline-sheet__primary"
            disabled={!isOnline || isSyncing}
            onClick={syncNow}
          >
            {isSyncing ? "Syncing…" : "Sync Now"}
          </button>
          <button
            type="button"
            className="offline-sheet__danger"
            disabled={!isOnline}
            onClick={clear}
          >
            {isConfirmingClear ? "Tap Again to Clear" : "Clear Offline Data"}
          </button>
          <button type="button" className="offline-sheet__cancel" onClick={onClose}>
            Go Back
          </button>
        </div>
      </div>
    </div>
  )
}
